import { useEffect, useMemo, useState } from 'react';
import {
  adminShareholders,
  adminShareholderTxns,
  type Shareholder,
  type ShareholderTxn,
  type ShareholderInput,
  type ShareholderTxnInput,
} from '../../api/admin';
import { Plus, Pencil, Trash2, Download, MessageSquare } from 'lucide-react';

const PAYMENT_METHODS = ['UPI', 'Cash', 'Card', 'Bank transfer', 'Other'];

const fmt = (n: number) => `₹${(Number.isFinite(n) ? n : 0).toLocaleString('en-IN')}`;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayISO = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
const dateShort = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';

// Group a raw numeric string with Indian commas for display.
const groupIN = (raw: string) => {
  if (!raw) return '';
  const clean = raw.replace(/[^0-9]/g, '');
  return clean ? Number(clean).toLocaleString('en-IN') : '';
};

function downloadCSV(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface SForm { id: string | null; name: string; comment: string }
const emptyShareholder: SForm = { id: null, name: '', comment: '' };

interface TForm {
  id: string | null;
  shareholder_id: string;
  paid_to: string;
  amount: string;
  txn_date: string;
  payment_method: string;
  comment: string;
}

export default function ShareholdersTab({ isDark }: { isDark: boolean }) {
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [txns, setTxns] = useState<ShareholderTxn[]>([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<'invested' | 'settle'>('invested');
  const [shModal, setShModal] = useState<SForm | null>(null);
  const [shSaving, setShSaving] = useState(false);
  const [shError, setShError] = useState('');

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const inp = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
           : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  const load = () => {
    setLoading(true);
    Promise.all([adminShareholders.list(), adminShareholderTxns.list()])
      .then(([s, t]) => { setShareholders(s); setTxns(t); })
      .catch(() => { setShareholders([]); setTxns([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Per-shareholder aggregates for the summary.
  const summary = useMemo(() => [...shareholders]
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map((s) => {
      let invested = 0, settled = 0;
      for (const t of txns) {
        if (t.shareholder_id !== s.id) continue;
        if (t.kind === 'invested') invested += t.amount;
        else if (t.kind === 'settle') settled += t.amount;
      }
      return { ...s, invested, settled, net: invested - settled };
    }), [shareholders, txns]);

  const nameOf = (id: string) => shareholders.find((s) => s.id === id)?.name ?? '—';

  // ── Shareholder CRUD ──
  const saveShareholder = async () => {
    if (!shModal) return;
    if (!shModal.name.trim()) { setShError('Enter a name'); return; }
    setShSaving(true);
    setShError('');
    const payload: ShareholderInput = { name: shModal.name.trim(), comment: shModal.comment || null };
    try {
      if (shModal.id) await adminShareholders.update(shModal.id, payload);
      else await adminShareholders.create(payload);
      setShModal(null);
      load();
    } catch (e) {
      setShError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setShSaving(false);
    }
  };
  const removeShareholder = async (s: Shareholder) => {
    if (!confirm(`Delete "${s.name}"? This also removes all their investment & settlement records.`)) return;
    try {
      await adminShareholders.delete(s.id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Summary ── */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div>
              <h2 className="font-semibold">Shareholders</h2>
              <p className="text-xs text-gray-500 mt-0.5">Invested / Settled / Net totals are all-time</p>
            </div>
          <button
            onClick={() => { setShModal({ ...emptyShareholder }); setShError(''); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg"
          >
            <Plus size={15} /> Add Shareholder
          </button>
        </div>
        <div className={`rounded-xl border overflow-x-auto ${card}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
                <th className="font-medium py-3 px-4">Shareholder</th>
                <th className="font-medium py-3 px-4 text-right">Invested</th>
                <th className="font-medium py-3 px-4 text-right">Settled</th>
                <th className="font-medium py-3 px-4 text-right">Net</th>
                <th className="font-medium py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && summary.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-gray-400">No shareholders yet</td></tr>}
              {summary.map((s) => (
                <tr key={s.id} className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <td className="py-3 px-4">
                    <div className="font-medium">{s.name}</div>
                    {s.comment && (
                      <p className={`text-[11px] italic mt-0.5 flex items-start gap-1 max-w-[240px] break-words ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <MessageSquare size={11} className="mt-0.5 shrink-0" /> {s.comment}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">{fmt(s.invested)}</td>
                  <td className="py-3 px-4 text-right">{fmt(s.settled)}</td>
                  <td className={`py-3 px-4 text-right font-semibold ${s.net > 0 ? 'text-emerald-500' : s.net < 0 ? 'text-red-500' : ''}`}>{fmt(s.net)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => { setShModal({ id: s.id, name: s.name, comment: s.comment || '' }); setShError(''); }} title="Edit" aria-label="Edit" className="text-gray-400 hover:text-emerald-500"><Pencil size={15} /></button>
                      <button onClick={() => removeShareholder(s)} title="Delete" aria-label="Delete" className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Sub-tabs ── */}
      <div>
        <div className="flex items-center gap-1 mb-4">
          {([['invested', 'Investment'], ['settle', 'Settlement']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSubTab(k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                subTab === k
                  ? 'bg-emerald-500 text-white'
                  : isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <TxnLedger
          kind={subTab}
          isDark={isDark}
          card={card}
          inp={inp}
          shareholders={shareholders}
          txns={txns}
          nameOf={nameOf}
          reload={load}
        />
      </div>

      {/* ── Shareholder modal ── */}
      {shModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShModal(null)}>
          <div className={`w-full max-w-md rounded-2xl border p-6 ${card}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{shModal.id ? 'Edit' : 'Add'} Shareholder</h3>
            {shError && <div className="mb-3 text-sm text-red-500">{shError}</div>}
            <div className="space-y-3">
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Name <span className="text-red-500">*</span></label>
                <input type="text" value={shModal.name} onChange={(e) => setShModal({ ...shModal, name: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Comment (optional)</label>
                <textarea value={shModal.comment} onChange={(e) => setShModal({ ...shModal, comment: e.target.value })} className={inp} rows={2} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={saveShareholder} disabled={shSaving} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">{shSaving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setShModal(null)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transaction ledger (shared by Investment & Settlement sub-tabs) ──
function TxnLedger({ kind, isDark, card, inp, shareholders, txns, nameOf, reload }: {
  kind: 'invested' | 'settle';
  isDark: boolean;
  card: string;
  inp: string;
  shareholders: Shareholder[];
  txns: ShareholderTxn[];
  nameOf: (id: string) => string;
  reload: () => void;
}) {
  const [datePreset, setDatePreset] = useState<'all' | 'week' | 'month' | 'year' | 'custom'>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [modal, setModal] = useState<TForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const label = kind === 'invested' ? 'Investment' : 'Settlement';

  const sortedShareholders = useMemo(
    () => [...shareholders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [shareholders],
  );

  const inRange = (dateStr: string) => {
    const t = new Date(dateStr).getTime();
    if (Number.isNaN(t)) return true;
    const now = Date.now();
    const DAY = 86400000;
    if (datePreset === 'week') return t >= now - 7 * DAY;
    if (datePreset === 'month') return t >= now - 30 * DAY;
    if (datePreset === 'year') return t >= now - 365 * DAY;
    if (datePreset === 'custom') {
      if (customFrom && t < new Date(customFrom).getTime()) return false;
      if (customTo && t > new Date(customTo).getTime() + DAY) return false;
      return true;
    }
    return true;
  };

  const rows = useMemo(
    () => txns.filter((t) => t.kind === kind && inRange(t.txn_date))
      .sort((a, b) => new Date(b.txn_date ?? b.created_at).getTime() - new Date(a.txn_date ?? a.created_at).getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [txns, kind, datePreset, customFrom, customTo],
  );
  const total = useMemo(() => rows.reduce((s, r) => s + r.amount, 0), [rows]);

  const num = (s: string) => { const n = parseInt(s, 10); return Number.isNaN(n) ? 0 : n; };

  const save = async () => {
    if (!modal) return;
    if (!modal.shareholder_id) { setError('Select a shareholder'); return; }
    if (!modal.paid_to.trim()) { setError('Enter who it was paid to'); return; }
    const amt = num(modal.amount);
    if (amt <= 0) { setError('Enter a valid amount'); return; }
    setSaving(true);
    setError('');
    const payload: ShareholderTxnInput = {
      shareholder_id: modal.shareholder_id,
      kind,
      amount: amt,
      paid_to: modal.paid_to.trim(),
      txn_date: modal.txn_date || null,
      payment_method: modal.payment_method || null,
      comment: modal.comment || null,
    };
    try {
      if (modal.id) await adminShareholderTxns.update(modal.id, payload);
      else await adminShareholderTxns.create(payload);
      setModal(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(`Delete this ${label.toLowerCase()} record?`)) return;
    try {
      await adminShareholderTxns.delete(id);
      reload();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const exportCsv = () => {
    const header = ['Date', 'Shareholder', 'Paid to', 'Payment method', 'Comment', 'Amount'];
    const csvRows = rows.map((r) => [
      r.txn_date ? r.txn_date.slice(0, 10) : '', nameOf(r.shareholder_id), r.paid_to ?? '',
      r.payment_method ?? '', r.comment ?? '', r.amount,
    ]);
    downloadCSV(`ZeroSmoke_${label}s.csv`, header, csvRows);
  };

  const openAdd = () => {
    setModal({ id: null, shareholder_id: sortedShareholders[0]?.id ?? '', paid_to: '', amount: '', txn_date: todayISO(), payment_method: 'UPI', comment: '' });
    setError('');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg">
          <Plus size={16} /> Add {label}
        </button>
        <button onClick={exportCsv}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
          <Download size={15} /> Export CSV
        </button>
        <span className={`ml-auto text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total: <b>{fmt(total)}</b></span>
      </div>

      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Date:</span>
        {([['all', 'All time'], ['week', 'Last week'], ['month', 'Last month'], ['year', 'Last year'], ['custom', 'Custom']] as const).map(([v, l]) => (
          <button
            key={v}
            onClick={() => setDatePreset(v)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              datePreset === v
                ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
                : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {l}
          </button>
        ))}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>–</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`px-2 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`} />
          </div>
        )}
      </div>

      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
              <th className="font-medium py-3 px-4">Date</th>
              <th className="font-medium py-3 px-4">Shareholder</th>
              <th className="font-medium py-3 px-4">Paid to</th>
              <th className="font-medium py-3 px-4">Payment</th>
              <th className="font-medium py-3 px-4 text-right">Amount</th>
              <th className="font-medium py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">No {label.toLowerCase()} records</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                <td className="py-3 px-4">{dateShort(r.txn_date)}</td>
                <td className="py-3 px-4">
                  <div className="max-w-[240px] break-words">{nameOf(r.shareholder_id)}</div>
                  {r.comment && (
                    <p className={`text-[11px] italic mt-1 flex items-start gap-1 max-w-[240px] break-words ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <MessageSquare size={11} className="mt-0.5 shrink-0" /> {r.comment}
                    </p>
                  )}
                </td>
                <td className="py-3 px-4"><div className="max-w-[200px] break-words">{r.paid_to || '—'}</div></td>
                <td className="py-3 px-4">{r.payment_method || '—'}</td>
                <td className="py-3 px-4 text-right font-medium">{fmt(r.amount)}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={() => { setModal({ id: r.id, shareholder_id: r.shareholder_id, paid_to: r.paid_to || '', amount: String(r.amount), txn_date: r.txn_date ? r.txn_date.slice(0, 10) : todayISO(), payment_method: r.payment_method || 'UPI', comment: r.comment || '' }); setError(''); }} title="Edit" aria-label="Edit" className="text-gray-400 hover:text-emerald-500"><Pencil size={15} /></button>
                    <button onClick={() => remove(r.id)} title="Delete" aria-label="Delete" className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <div className={`w-full max-w-md rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${card}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{modal.id ? 'Edit' : 'Add'} {label}</h3>
            {error && <div className="mb-3 text-sm text-red-500">{error}</div>}
            <div className="space-y-3">
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Shareholder <span className="text-red-500">*</span></label>
                <select value={modal.shareholder_id} onChange={(e) => setModal({ ...modal, shareholder_id: e.target.value })} className={inp}>
                  <option value="">Select a shareholder…</option>
                  {sortedShareholders.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Paid to <span className="text-red-500">*</span></label>
                <input type="text" value={modal.paid_to} onChange={(e) => setModal({ ...modal, paid_to: e.target.value })} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Amount (₹) <span className="text-red-500">*</span></label>
                  <input type="text" inputMode="numeric" value={groupIN(modal.amount)} onChange={(e) => setModal({ ...modal, amount: e.target.value.replace(/[^0-9]/g, '') })} className={inp} placeholder="0" />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</label>
                  <input type="date" value={modal.txn_date} onChange={(e) => setModal({ ...modal, txn_date: e.target.value })} className={inp} />
                </div>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment method</label>
                <select value={modal.payment_method} onChange={(e) => setModal({ ...modal, payment_method: e.target.value })} className={inp}>
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Comment (optional)</label>
                <textarea value={modal.comment} onChange={(e) => setModal({ ...modal, comment: e.target.value })} className={inp} rows={2} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setModal(null)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
